'use client';

import { Fragment, useEffect, useRef, useState, type FC, type KeyboardEvent } from 'react';
import { styled } from '@mui/material/styles';
import { withSe3AssetPrefix } from 'components/HomepageV3/assetPrefix';
import { FONTS, GRADIENTS } from 'components/HomepageV3/theme/tokens';
import {
  buildSe3ExploreRoute,
  useSe3QaSearch,
  withSe3QaOverride,
} from 'components/HomepageV3/utils/routes';
import { useSafeFeatureEnabled, useSafeFeatureFlagVariantKey } from 'hooks/useSafeFeatureFlag';
import { investUrl, userMenu } from 'utils/links';

export type EditorialNavVariant = 'loggedOut' | 'loggedIn' | 'authPending';

export interface EditorialNavLink {
  label: string;
  href: string;
  primary?: boolean;
  group?: string;
}

export interface EditorialNavSearchSuggestion {
  label: string;
  /** Additional terms that find the offering while preserving its display label. */
  searchTerms?: string[];
}

export interface EditorialNavProps {
  variant?: EditorialNavVariant;
  links?: EditorialNavLink[];
  dropdownLabel?: string;
  dropdownItems?: EditorialNavLink[];
  investDropdownItems?: EditorialNavLink[];
  searchPlaceholder?: string;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: (value: string) => void;
  searchSuggestions?: EditorialNavSearchSuggestion[];
  accountName?: string;
  accountMenuItems?: EditorialNavLink[];
  accountUserName?: string;
  accountUserEmail?: string;
  accountUserSummary?: string;
  isIssuer?: boolean;
  logInLabel?: string;
  signUpLabel?: string;
  onSearchClick?: () => void;
  onLogInClick?: () => void;
  onSignUpClick?: () => void;
  onAccountClick?: () => void;
  className?: string;
  'data-cy'?: string;
}

const DEFAULT_LINKS: EditorialNavLink[] = [
  { label: 'Invest', href: buildSe3ExploreRoute(), primary: true },
  { label: 'Pre-IPO Deals', href: '/private' },
  { label: 'Marketplace', href: 'https://marketplace.startengine.com' },
  { label: 'Learn', href: '/blog' },
];

const DEFAULT_LOGGED_IN_LINKS: EditorialNavLink[] = [
  { label: 'Invest', href: buildSe3ExploreRoute(), primary: true },
  { label: 'Pre-IPO Deals', href: '/private' },
  { label: 'Marketplace', href: 'https://marketplace.startengine.com' },
  { label: 'Learn', href: '/blog' },
  { label: 'Portfolio', href: `${userMenu.portfolio}?se3=1` },
];

const DEFAULT_DROPDOWN: EditorialNavLink[] = [
  {
    label: 'Pre-IPO Education',
    href: 'https://www.startengine.com/insights/private-market-education',
  },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact Us', href: '/contact-us?source=header' },
];

export const DEFAULT_ACCOUNT_MENU: EditorialNavLink[] = [
  { label: 'Portfolio', group: 'Portfolio', href: `${userMenu.portfolio}?se3=1` },
  { label: 'Watchlist', group: 'Portfolio', href: `${investUrl}/account/watchlist?se3=1` },
  {
    label: 'Past Transactions',
    group: 'Portfolio',
    href: `${investUrl}/account/portfolio/order-history?se3=1`,
  },
  {
    label: 'Open Orders',
    group: 'Portfolio',
    href: `${investUrl}/account/portfolio?se3=1#transactions-in-progress`,
  },
  {
    label: 'Documents',
    group: 'Portfolio',
    href: `${investUrl}/account/portfolio?se3=1#documents`,
  },
  {
    label: 'Statements',
    group: 'Portfolio',
    href: `${investUrl}/account/portfolio?se3=1&documentTab=statements#documents`,
  },
  {
    label: 'Tax documents',
    group: 'Portfolio',
    href: `${investUrl}/account/portfolio?se3=1&documentTab=tax#documents`,
  },
  {
    label: 'Ownership transfers',
    group: 'Portfolio',
    href: `${investUrl}/account/portfolio/transfers?se3=1`,
  },
  { label: 'Banking', group: 'Account', href: `${investUrl}/account/banking?se3=1` },
  {
    label: 'Beneficiaries',
    group: 'Account',
    href: `${investUrl}/account/portfolio/beneficiaries?se3=1`,
  },
  { label: 'Venture Club', group: 'Account', href: userMenu.ventureClub || '/venture-club' },
  { label: 'Settings', group: 'Account', href: userMenu.settings },
  { label: 'Help Center', group: 'Support', href: '/contact-us?source=header' },
  { label: 'Log Out', group: 'Support', href: userMenu.logout },
];

const WORDMARK_SRC = withSe3AssetPrefix('/homepage-v3/se-wordmark.png');

const INVEST_IN_STARTENGINE_URL = 'https://invest.startengine.com';

const Shell = styled('header')({
  position: 'sticky',
  top: 0,
  zIndex: 1200,
  background: GRADIENTS.topSurface,
});

const Nav = styled('nav')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px var(--pad)',
  borderBottom: '1px solid rgba(233,215,182,0.12)',
  '@media (min-width: 641px) and (max-width: 1200px)': {
    padding: '20px 24px',
  },
  '@media (max-width: 640px)': {
    padding: '14px var(--pad)',
  },
});

const Left = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 44,
  '@media (min-width: 641px) and (max-width: 1200px)': { gap: 0 },
  '@media (max-width: 640px)': { gap: 0 },
});

const Brand = styled('a')({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  flexShrink: 0,
});

const BrandImg = styled('img')({
  height: 30,
  width: 'auto',
  display: 'block',
});

const Links = styled('div')({
  display: 'flex',
  gap: 30,
  alignItems: 'center',
  flexWrap: 'nowrap',
  '@media (min-width: 641px) and (max-width: 1200px)': { display: 'none' },
  '@media (max-width: 640px)': { display: 'none' },
});

const navLinkBase = {
  fontFamily: FONTS.sans,
  fontSize: '13.5px',
  fontWeight: 500,
  letterSpacing: '0.16em',
  textTransform: 'uppercase' as const,
  color: 'var(--c-text-2)',
  textDecoration: 'none',
  transition: 'color 0.15s',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:hover': { color: 'var(--champagne)' },
};

const NavLink = styled('a', {
  shouldForwardProp: (prop) => prop !== 'primary',
})<{ primary?: boolean }>(({ primary }) => ({
  ...navLinkBase,
  ...(primary ? { color: 'var(--champagne)' } : null),
}));

const InvestInStartEngineLink = styled('a')({
  fontFamily: FONTS.sans,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--night)',
  background: 'var(--champagne)',
  padding: '12px 20px',
  borderRadius: 14,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  transition: 'background 0.15s',
  '&:hover': { background: '#f3e6cb' },
});
const Dropdown = styled('div')({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    height: 22,
  },
  '&:hover .se-nav-dd-menu, &:focus-within .se-nav-dd-menu': {
    opacity: 1,
    visibility: 'visible',
    transform: 'translateY(0)',
  },
  '&:hover .se-nav-link-dd svg, &:focus-within .se-nav-link-dd svg': {
    transform: 'rotate(180deg)',
    opacity: 1,
  },
  // On mobile the hamburger owns the account menu; hide the standalone
  // "My Account" dropdown so it isn't a duplicative second menu (Paige QA 2026-07-10).
  '@media (max-width: 1200px)': {
    '&.se-account-dd': { display: 'none' },
  },
});

const DropdownTrigger = styled('a')({
  ...navLinkBase,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  '& svg': {
    width: 9,
    height: 9,
    opacity: 0.7,
    transition: 'transform 0.18s',
  },
});

const PrimaryDropdownTrigger = styled(DropdownTrigger)({
  color: 'var(--champagne)',
});

const DropdownMenu = styled('div')({
  position: 'absolute',
  top: '100%',
  left: -16,
  marginTop: 14,
  minWidth: 200,
  background: 'var(--night-deep)',
  border: '1px solid rgba(233,215,182,0.18)',
  borderRadius: 14,
  padding: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  opacity: 0,
  visibility: 'hidden',
  transform: 'translateY(-6px)',
  transition: 'opacity 0.16s ease, transform 0.16s ease, visibility 0.16s',
  boxShadow: '0 22px 50px rgba(0,0,0,0.5)',
  zIndex: 200,
  '& a': {
    fontFamily: FONTS.sans,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--c-text-2)',
    textDecoration: 'none',
    padding: '11px 14px',
    borderRadius: 9,
    transition: 'background 0.14s, color 0.14s',
    '&:hover': {
      background: 'rgba(233,215,182,0.08)',
      color: 'var(--champagne)',
    },
  },
});

const AccountMenu = styled(DropdownMenu, {
  shouldForwardProp: (prop) => prop !== 'grouped' && prop !== 'open',
})<{ grouped?: boolean; open?: boolean }>(({ grouped, open }) => ({
  left: 'auto',
  right: -8,
  ...(grouped
    ? {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        alignItems: 'start',
        width: 440,
        maxWidth: 'calc(100vw - 48px)',
        gap: '12px 20px',
        padding: 16,
        maxHeight: 'calc(100dvh - 100px)',
        overflowY: 'auto',
        opacity: open ? 1 : 0,
        visibility: open ? 'visible' : 'hidden',
        transform: 'none',
        transition: 'none',
      }
    : null),
}));

const AccountIdentity = styled('div')({
  gridColumn: '1 / -1',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '6px 12px 14px',
  marginBottom: 4,
  borderBottom: '1px solid rgba(233,215,182,0.18)',
});

const AccountAvatar = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 'none',
  width: 38,
  height: 38,
  borderRadius: '50%',
  background: 'var(--champagne)',
  color: 'var(--night)',
  fontFamily: FONTS.sans,
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '0.02em',
});

const AccountIdentityText = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

const AccountIdentityName = styled('span')({
  color: '#fff',
  fontFamily: FONTS.sans,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: '20px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const AccountIdentityMeta = styled('span')({
  color: 'var(--c-text-3)',
  fontFamily: FONTS.sans,
  fontSize: 12,
  lineHeight: '18px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const accountInitials = (name?: string, email?: string) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (
    String(email || '')
      .charAt(0)
      .toUpperCase() || '•'
  );
};

const AccountGroup = styled('div', {
  shouldForwardProp: (prop) => prop !== 'spanRows',
})<{ spanRows?: boolean }>(({ spanRows }) => ({
  '& + &': { marginTop: 12 },
  ...(spanRows ? { gridRow: 'span 2' } : {}),
  '&& a': {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    minHeight: 44,
    padding: '10px 12px',
    border: 0,
    fontSize: 14,
    lineHeight: '24px',
    letterSpacing: 0,
    textTransform: 'none',
    '&:focus-visible': {
      outline: '2px solid var(--champagne)',
      outlineOffset: '-2px',
      borderRadius: 6,
    },
  },
}));

const AccountGroupHeading = styled('div')({
  padding: '4px 12px',
  color: 'var(--c-text-3)',
  fontFamily: FONTS.sans,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '20px',
});

const Right = styled('div')({
  display: 'flex',
  gap: 12,
  alignItems: 'center',
  '@media (min-width: 641px) and (max-width: 1200px)': { gap: 9 },
  '@media (max-width: 640px)': { gap: 9 },
});

const Search = styled('form')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 14px',
  borderRadius: 999,
  border: '1px solid rgba(233,215,182,0.28)',
  background: 'rgba(0,0,0,0.2)',
  transition: 'border-color 0.15s, background 0.15s',
  '&:focus-within, &:hover': {
    borderColor: 'var(--champagne)',
    background: 'rgba(0,0,0,0.32)',
  },
  '& svg': {
    width: 15,
    height: 15,
    color: 'var(--c-text-3)',
    flex: 'none',
  },
  '@media (max-width: 640px)': { display: 'none' },
});

const SearchSuggestions = styled('div')({
  position: 'absolute',
  top: 'calc(100% + 8px)',
  left: 0,
  right: 0,
  zIndex: 2,
  overflow: 'hidden',
  border: '1px solid rgba(233,215,182,0.3)',
  borderRadius: 12,
  background: 'var(--night)',
  boxShadow: '0 14px 28px rgba(0,0,0,0.3)',
});

const SearchSuggestion = styled('button')({
  display: 'block',
  width: '100%',
  padding: '10px 13px',
  border: 0,
  borderBottom: '1px solid rgba(233,215,182,0.1)',
  background: 'transparent',
  color: 'var(--cream)',
  fontFamily: FONTS.sans,
  fontSize: 13,
  textAlign: 'left',
  cursor: 'pointer',
  '&:last-child': { borderBottom: 0 },
  '&:hover, &:focus-visible': { background: 'rgba(233,215,182,0.12)', outline: 'none' },
});

const SearchInput = styled('input')({
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: '#fff',
  fontFamily: FONTS.sans,
  fontSize: 13,
  width: 150,
  '@media (min-width: 641px) and (max-width: 1200px)': { width: 118 },
  '&::placeholder': { color: 'var(--c-text-3)' },
});

const MobileSearch = styled('form')({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  alignItems: 'center',
  gap: 8,
  padding: '10px 0 14px',
  borderBottom: '1px solid rgba(233,215,182,0.08)',
  '& svg': {
    width: 15,
    height: 15,
    color: 'var(--c-text-3)',
  },
});

const MobileSearchField = styled('label')({
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '11px 13px',
  borderRadius: 12,
  border: '1px solid rgba(233,215,182,0.28)',
  background: 'rgba(0,0,0,0.24)',
  '&:focus-within': {
    borderColor: 'var(--champagne)',
    background: 'rgba(0,0,0,0.34)',
  },
});

const MobileSearchInput = styled('input')({
  minWidth: 0,
  width: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: '#fff',
  fontFamily: FONTS.sans,
  fontSize: 13,
  '&::placeholder': { color: 'var(--c-text-3)' },
});

const MobileSearchButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 42,
  padding: '0 14px',
  borderRadius: 12,
  border: '1px solid rgba(233,215,182,0.36)',
  background: 'var(--champagne)',
  color: 'var(--night)',
  fontFamily: FONTS.sans,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  cursor: 'pointer',
});

const GhostButton = styled('button')({
  fontFamily: FONTS.sans,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: '#fff',
  background: 'transparent',
  border: '1px solid rgba(233,215,182,0.3)',
  padding: '11px 18px',
  borderRadius: 14,
  cursor: 'pointer',
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
  '&:hover': { borderColor: 'var(--champagne)', color: 'var(--champagne)' },
  '@media (min-width: 641px) and (max-width: 1200px)': {
    padding: '11px 14px',
    letterSpacing: '0.11em',
  },
  '@media (max-width: 640px)': { display: 'none' },
});

const SolidButton = styled('button')({
  fontFamily: FONTS.sans,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--night)',
  background: 'var(--champagne)',
  border: 'none',
  padding: '12px 20px',
  borderRadius: 14,
  cursor: 'pointer',
  transition: 'background 0.15s',
  whiteSpace: 'nowrap',
  '&:hover': { background: '#f3e6cb' },
  '@media (min-width: 641px) and (max-width: 1200px)': {
    padding: '12px 15px',
    letterSpacing: '0.11em',
  },
  '@media (max-width: 640px)': {
    boxSizing: 'border-box',
    minHeight: 44,
    padding: '9px 13px',
    fontSize: 10,
    letterSpacing: '0.1em',
  },
});

const MenuButton = styled('button')({
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 9,
  background: 'transparent',
  border: '1px solid rgba(233,215,182,0.3)',
  borderRadius: 12,
  color: '#fff',
  cursor: 'pointer',
  '& svg': { width: 18, height: 18, display: 'block' },
  '@media (min-width: 641px) and (max-width: 1200px)': { display: 'inline-flex' },
  '@media (max-width: 640px)': {
    display: 'inline-flex',
    boxSizing: 'border-box',
    width: 44,
    height: 44,
    padding: 0,
  },
});

const MobileMenu = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ open }) => ({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: open ? 'none' : 0,
  padding: open ? '4px var(--pad) 14px' : '0 var(--pad)',
  overflow: 'hidden',
  visibility: open ? 'visible' : 'hidden',
  pointerEvents: open ? 'auto' : 'none',
  background: 'var(--night-deep)',
  borderBottom: open ? '1px solid rgba(233,215,182,0.12)' : 'none',
  '@media (min-width: 1201px)': { display: 'none' },
  '& > a, & > button': {
    padding: '13px 2px',
    fontFamily: FONTS.sans,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: 'var(--c-text-2)',
    textDecoration: 'none',
    background: 'transparent',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(233,215,182,0.08)',
    '&:last-of-type': { borderBottom: 'none' },
    '&:active': { color: 'var(--champagne)' },
  },
  '& > .se-mobile-sub': {
    paddingLeft: 16,
    fontSize: 11,
    color: 'var(--gold)',
    borderLeft: '2px solid var(--gold)',
    marginLeft: 2,
  },
  '& > .se-mobile-group-label': {
    padding: '12px 2px 2px',
    fontFamily: FONTS.sans,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
    color: 'var(--c-text-3)',
    borderBottom: 'none',
  },
  '& > [data-cy="editorial-nav-mobile-account-identity"]': {
    padding: '10px 2px 14px',
    marginBottom: 6,
  },
  '& > .se-mobile-cta': {
    color: 'var(--champagne)',
    fontWeight: 600,
  },
}));

const Account = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  padding: 0,
  color: '#fff',
  '& .se-account-caret': {
    display: 'flex',
    alignItems: 'center',
    color: 'rgba(255,255,255,0.7)',
  },
  '& .se-account-caret svg': { width: 16, height: 16, display: 'block' },
});

const AccountName = styled('span')({
  fontFamily: FONTS.sans,
  fontSize: 14,
  fontWeight: 500,
  color: '#fff',
  letterSpacing: '0.01em',
});

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  );
}

function CaretIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function HamburgerIcon() {
  return (
    <svg
      data-cy="editorial-nav-menu-icon-open"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      data-cy="editorial-nav-menu-icon-close"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

const EditorialNav: FC<EditorialNavProps> = ({
  variant = 'loggedOut',
  links,
  dropdownLabel = 'Learn',
  dropdownItems = DEFAULT_DROPDOWN,
  investDropdownItems,
  searchPlaceholder = 'Search offerings',
  showSearch = true,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  searchSuggestions = [],
  accountName = 'My Account',
  accountMenuItems = DEFAULT_ACCOUNT_MENU,
  accountUserName = '',
  accountUserEmail = '',
  accountUserSummary = '',
  isIssuer = false,
  logInLabel = 'Log in',
  signUpLabel = 'Create account',
  onSearchClick,
  onLogInClick,
  onSignUpClick,
  onAccountClick,
  className,
  'data-cy': dataCy = 'editorial-nav',
}) => {
  const qaSearch = useSe3QaSearch();
  const showInvestCta = useSafeFeatureEnabled('invest-in-startengine-cta');
  const ownershipTransfersEnabled =
    useSafeFeatureFlagVariantKey('portfolio-ownership-transfers') === 'enabled';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState('');
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLFormElement>(null);
  const [searchSuggestionsOpen, setSearchSuggestionsOpen] = useState(false);
  const isControlled = searchValue !== undefined;
  const value = isControlled ? searchValue : internalSearch;
  const normalizedSearch = value.trim().toLowerCase();
  const matchedSuggestions = normalizedSearch
    ? Array.from(
        new Map(
          searchSuggestions
            .filter((suggestion) =>
              [suggestion.label, ...(suggestion.searchTerms ?? [])].some((term) =>
                term.toLowerCase().includes(normalizedSearch),
              ),
            )
            .map((suggestion) => [suggestion.label.toLowerCase(), suggestion]),
        ).values(),
      ).slice(0, 5)
    : [];
  const isLoggedIn = variant === 'loggedIn';
  const showLoggedOutActions = variant === 'loggedOut';
  const resolvedLinks = links ?? (isLoggedIn ? DEFAULT_LOGGED_IN_LINKS : DEFAULT_LINKS);
  const resolvedInvestDropdownItems = investDropdownItems ?? [];
  const issuerDashboardItem = { label: 'Issuer Dashboard', href: userMenu.issuer };
  const gatedAccountMenuItems = ownershipTransfersEnabled
    ? accountMenuItems
    : accountMenuItems.filter((item) => !item.href?.includes('/account/portfolio/transfers'));
  const portfolioIndex = gatedAccountMenuItems.findIndex((item) => item.label === 'Portfolio');
  const issuerDashboardIndex = portfolioIndex >= 0 ? portfolioIndex + 1 : 0;
  const resolvedAccountMenuItems =
    isIssuer && !gatedAccountMenuItems.some((item) => item.href === userMenu.issuer)
      ? [
          ...gatedAccountMenuItems.slice(0, issuerDashboardIndex),
          { ...issuerDashboardItem, group: gatedAccountMenuItems[portfolioIndex]?.group },
          ...gatedAccountMenuItems.slice(issuerDashboardIndex),
        ]
      : gatedAccountMenuItems;
  const hasAccountGroups = resolvedAccountMenuItems.some((item) => item.group);
  const hasAccountIdentity = Boolean(accountUserName || accountUserEmail);
  const accountGroups = resolvedAccountMenuItems.reduce<
    { label?: string; items: EditorialNavLink[] }[]
  >((groups, item) => {
    const previous = groups[groups.length - 1];
    if (previous && previous.label === item.group) previous.items.push(item);
    else groups.push({ label: item.group, items: [item] });
    return groups;
  }, []);

  const renderAccountIdentity = (mobile = false) =>
    hasAccountIdentity ? (
      <AccountIdentity
        data-cy={
          mobile ? 'editorial-nav-mobile-account-identity' : 'editorial-nav-account-identity'
        }
      >
        <AccountAvatar aria-hidden="true">
          {accountInitials(accountUserName, accountUserEmail)}
        </AccountAvatar>
        <AccountIdentityText>
          {accountUserName && <AccountIdentityName>{accountUserName}</AccountIdentityName>}
          {accountUserEmail && <AccountIdentityMeta>{accountUserEmail}</AccountIdentityMeta>}
          {accountUserSummary && <AccountIdentityMeta>{accountUserSummary}</AccountIdentityMeta>}
        </AccountIdentityText>
      </AccountIdentity>
    ) : null;

  const renderAccountItems = (mobile = false) => {
    const renderLink = (item: EditorialNavLink) => (
      <a
        className={mobile ? 'se-mobile-sub' : undefined}
        href={item.href}
        key={item.label}
        data-cy={mobile ? 'editorial-nav-mobile-link' : 'editorial-nav-account-item'}
        onClick={() => {
          setAccountOpen(false);
          setMobileOpen(false);
        }}
      >
        {item.label}
      </a>
    );
    if (!hasAccountGroups) return resolvedAccountMenuItems.map(renderLink);
    return accountGroups.map((group, index) =>
      mobile ? (
        <Fragment key={`${group.label || 'links'}-${group.items[0].label}`}>
          {group.label && <div className="se-mobile-group-label">{group.label}</div>}
          {group.items.map(renderLink)}
        </Fragment>
      ) : (
        <AccountGroup
          key={`${group.label || 'links'}-${group.items[0].label}`}
          role="group"
          aria-label={group.label}
          spanRows={index === 0}
        >
          {group.label && <AccountGroupHeading>{group.label}</AccountGroupHeading>}
          {group.items.map(renderLink)}
        </AccountGroup>
      ),
    );
  };

  const handleSearchChange = (next: string) => {
    if (!isControlled) setInternalSearch(next);
    setSearchSuggestionsOpen(true);
    onSearchChange?.(next);
  };

  const submitSearch = (closeMenu = false) => {
    onSearchSubmit?.(value);
    if (closeMenu) setMobileOpen(false);
  };

  const showDropdown = Boolean(dropdownLabel && dropdownItems.length > 0);
  const dropdownTriggerHref = dropdownItems[0]?.href ?? '/blog';
  const orderedLinks =
    showDropdown && !resolvedLinks.some((link) => link.label === dropdownLabel)
      ? [...resolvedLinks, { label: dropdownLabel, href: dropdownTriggerHref }]
      : resolvedLinks;
  const mobileLinks = orderedLinks.flatMap((link) => {
    if (showDropdown && link.label === dropdownLabel) return dropdownItems;
    if (link.label === 'Invest') return [link, ...resolvedInvestDropdownItems];
    return [link];
  });

  const handleShellKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape') return;
    if (accountOpen) {
      event.stopPropagation();
      setAccountOpen(false);
      accountButtonRef.current?.focus();
      return;
    }
    if (!mobileOpen) return;
    event.stopPropagation();
    setMobileOpen(false);
    menuButtonRef.current?.focus();
  };

  useEffect(() => {
    const closeSuggestionsOnOutsidePointerDown = (event: MouseEvent) => {
      if (searchRef.current?.contains(event.target as Node)) return;
      setSearchSuggestionsOpen(false);
    };

    document.addEventListener('mousedown', closeSuggestionsOnOutsidePointerDown);
    return () => document.removeEventListener('mousedown', closeSuggestionsOnOutsidePointerDown);
  }, []);

  useEffect(() => {
    if (!accountOpen) return undefined;
    const closeOnOutsidePointerDown = (event: MouseEvent) => {
      if (accountRef.current?.contains(event.target as Node)) return;
      setAccountOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsidePointerDown);
    return () => document.removeEventListener('mousedown', closeOnOutsidePointerDown);
  }, [accountOpen]);

  return (
    <Shell className={className} data-cy={dataCy} onKeyDown={handleShellKeyDown}>
      <Nav aria-label="Primary" className="se-nav">
        <Left className="se-nav-left">
          <Brand
            href={withSe3QaOverride('/', qaSearch)}
            aria-label="StartEngine"
            data-cy="editorial-nav-brand"
            className="se-logo"
          >
            <BrandImg className="se-logo-img" src={WORDMARK_SRC} alt="StartEngine" />
          </Brand>
          <Links className="se-nav-links" data-cy="editorial-nav-links">
            {orderedLinks.map((link) => {
              if (showDropdown && link.label === dropdownLabel) {
                return (
                  <Dropdown key={link.label} className="se-nav-dd" data-cy="editorial-nav-dropdown">
                    <DropdownTrigger
                      href={withSe3QaOverride(dropdownTriggerHref, qaSearch)}
                      className="se-nav-link se-nav-link-dd"
                    >
                      {dropdownLabel}
                      <CaretIcon />
                    </DropdownTrigger>
                    <DropdownMenu className="se-nav-dd-menu" data-cy="editorial-nav-dropdown-menu">
                      {dropdownItems.map((item) => (
                        <a
                          href={withSe3QaOverride(item.href, qaSearch)}
                          key={item.label}
                          data-cy="editorial-nav-dropdown-item"
                        >
                          {item.label}
                        </a>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                );
              }

              if (link.label === 'Invest' && resolvedInvestDropdownItems.length > 0) {
                return (
                  <Dropdown
                    key={link.label}
                    className="se-nav-dd"
                    data-cy="editorial-nav-invest-dropdown"
                  >
                    <PrimaryDropdownTrigger
                      href={link.href}
                      className="se-nav-link se-nav-link-dd primary"
                    >
                      {link.label}
                      <CaretIcon />
                    </PrimaryDropdownTrigger>
                    <DropdownMenu
                      className="se-nav-dd-menu"
                      data-cy="editorial-nav-invest-dropdown-menu"
                    >
                      {resolvedInvestDropdownItems.map((item) => (
                        <a href={item.href} key={item.label} data-cy="editorial-nav-dropdown-item">
                          {item.label}
                        </a>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                );
              }

              return (
                <NavLink
                  key={link.label}
                  href={link.href}
                  primary={link.primary}
                  data-cy={
                    isLoggedIn && link.label === 'Portfolio'
                      ? 'editorial-nav-portfolio'
                      : 'editorial-nav-link'
                  }
                  className={link.primary ? 'se-nav-link primary' : 'se-nav-link'}
                >
                  {link.label}
                </NavLink>
              );
            })}
            {showInvestCta && (
              <InvestInStartEngineLink
                href={INVEST_IN_STARTENGINE_URL}
                data-cy="editorial-nav-invest-in-startengine"
              >
                Invest in StartEngine
              </InvestInStartEngineLink>
            )}
          </Links>
        </Left>

        <Right className="se-nav-right">
          {showSearch && (
            <Search
              ref={searchRef}
              role="search"
              data-cy="editorial-nav-search"
              className="se-search"
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch();
              }}
            >
              <SearchIcon />
              <SearchInput
                className="se-search-input"
                type="text"
                value={value}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={(event) => handleSearchChange(event.target.value)}
                onFocus={() => {
                  setSearchSuggestionsOpen(true);
                  onSearchClick?.();
                }}
                data-cy="editorial-nav-search-input"
              />
              {searchSuggestionsOpen && matchedSuggestions.length > 0 && (
                <SearchSuggestions data-cy="editorial-nav-search-suggestions">
                  {matchedSuggestions.map((suggestion) => (
                    <SearchSuggestion
                      key={suggestion.label}
                      type="button"
                      data-cy="editorial-nav-search-suggestion"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        handleSearchChange(suggestion.label);
                        setSearchSuggestionsOpen(false);
                        onSearchSubmit?.(suggestion.label);
                      }}
                    >
                      {suggestion.label}
                    </SearchSuggestion>
                  ))}
                </SearchSuggestions>
              )}
            </Search>
          )}

          {isLoggedIn && (
            <Dropdown
              className="se-account-dd"
              data-cy="editorial-nav-account-dropdown"
              ref={accountRef}
            >
              <Account
                ref={accountButtonRef}
                type="button"
                onClick={() => {
                  if (hasAccountGroups) setAccountOpen((open) => !open);
                  onAccountClick?.();
                }}
                aria-haspopup="true"
                aria-expanded={hasAccountGroups ? accountOpen : undefined}
                data-cy="editorial-nav-account"
                className="se-account se-nav-link-dd"
              >
                <AccountName className="se-account-name">{accountName}</AccountName>
                <span className="se-account-caret">
                  <CaretIcon />
                </span>
              </Account>
              <AccountMenu
                id="editorial-nav-account-menu"
                grouped={hasAccountGroups}
                open={accountOpen}
                className={hasAccountGroups ? undefined : 'se-nav-dd-menu'}
                data-cy="editorial-nav-account-menu"
              >
                {renderAccountIdentity()}
                {renderAccountItems()}
              </AccountMenu>
            </Dropdown>
          )}

          {showLoggedOutActions && (
            <>
              <GhostButton
                type="button"
                onClick={onLogInClick}
                data-cy="editorial-nav-login"
                className="se-btn-ghost"
              >
                {logInLabel}
              </GhostButton>
              <SolidButton
                type="button"
                onClick={onSignUpClick}
                data-cy="editorial-nav-signup"
                className="se-btn-solid"
              >
                {signUpLabel}
              </SolidButton>
            </>
          )}

          <MenuButton
            ref={menuButtonRef}
            type="button"
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
            aria-controls="editorial-nav-mobile-menu"
            onClick={() => setMobileOpen((open) => !open)}
            data-cy="editorial-nav-menu-toggle"
            className="se-nav-menu"
          >
            {mobileOpen ? <CloseIcon /> : <HamburgerIcon />}
          </MenuButton>
        </Right>
      </Nav>

      <MobileMenu
        id="editorial-nav-mobile-menu"
        open={mobileOpen}
        aria-hidden={!mobileOpen}
        inert={mobileOpen ? undefined : true}
        className={mobileOpen ? 'se-mobile-menu open' : 'se-mobile-menu'}
        data-cy="editorial-nav-mobile-menu"
      >
        {showSearch && (
          <MobileSearch
            role="search"
            data-cy="editorial-nav-mobile-search"
            onSubmit={(event) => {
              event.preventDefault();
              submitSearch(true);
            }}
          >
            <MobileSearchField>
              <SearchIcon />
              <MobileSearchInput
                type="text"
                value={value}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={(event) => handleSearchChange(event.target.value)}
                onFocus={onSearchClick}
                data-cy="editorial-nav-mobile-search-input"
              />
            </MobileSearchField>
            <MobileSearchButton type="submit" data-cy="editorial-nav-mobile-search-submit">
              Search
            </MobileSearchButton>
          </MobileSearch>
        )}
        {isLoggedIn ? (
          <>
            {orderedLinks
              .filter((link) => link.label !== 'Portfolio')
              .map((link) =>
                showDropdown && link.label === dropdownLabel ? (
                  <Fragment key={link.label}>
                    <a
                      href={withSe3QaOverride(dropdownTriggerHref, qaSearch)}
                      data-cy="editorial-nav-mobile-link"
                    >
                      {dropdownLabel}
                    </a>
                    {dropdownItems.map((item) => (
                      <a
                        className="se-mobile-sub"
                        href={withSe3QaOverride(item.href, qaSearch)}
                        key={item.label}
                        data-cy="editorial-nav-mobile-link"
                      >
                        {item.label}
                      </a>
                    ))}
                  </Fragment>
                ) : (
                  <a
                    href={withSe3QaOverride(link.href, qaSearch)}
                    key={link.label}
                    data-cy="editorial-nav-mobile-link"
                  >
                    {link.label}
                  </a>
                ),
              )}
            {showInvestCta && (
              <a
                className="se-mobile-cta"
                href={INVEST_IN_STARTENGINE_URL}
                data-cy="editorial-nav-mobile-invest-in-startengine"
              >
                Invest in StartEngine
              </a>
            )}
            {renderAccountIdentity(true)}
            {!hasAccountIdentity && (
              <button type="button" onClick={onAccountClick} data-cy="editorial-nav-mobile-link">
                {accountName}
              </button>
            )}
            {renderAccountItems(true)}
          </>
        ) : (
          <>
            {mobileLinks.map((link) => (
              <a
                href={withSe3QaOverride(link.href, qaSearch)}
                key={link.label}
                data-cy="editorial-nav-mobile-link"
              >
                {link.label}
              </a>
            ))}
            {showInvestCta && (
              <a
                href={INVEST_IN_STARTENGINE_URL}
                data-cy="editorial-nav-mobile-invest-in-startengine"
              >
                Invest in StartEngine
              </a>
            )}
          </>
        )}

        {showLoggedOutActions && (
          <>
            <button type="button" onClick={onLogInClick} data-cy="editorial-nav-mobile-link">
              {logInLabel}
            </button>
            <button type="button" onClick={onSignUpClick} data-cy="editorial-nav-mobile-link">
              {signUpLabel}
            </button>
          </>
        )}
      </MobileMenu>
    </Shell>
  );
};

export default EditorialNav;
